import { apiClient } from '../api/client';

let cachedPublicKey: CryptoKey | null = null;
let pendingKeyFetch: Promise<CryptoKey> | null = null;

function pemToArrayBuffer(pem: string): ArrayBuffer {
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function fetchPublicKey(): Promise<CryptoKey> {
  const response = await apiClient.get<{ publicKey: string }>('/auth/public-key');
  if (!response.data?.publicKey) {
    throw new Error('Failed to retrieve encryption key from server.');
  }
  return crypto.subtle.importKey(
    'spki',
    pemToArrayBuffer(response.data.publicKey),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );
}

async function getPublicKey(): Promise<CryptoKey> {
  if (cachedPublicKey) {
    return cachedPublicKey;
  }
  if (!pendingKeyFetch) {
    pendingKeyFetch = fetchPublicKey()
      .then((key) => {
        cachedPublicKey = key;
        return key;
      })
      .finally(() => {
        pendingKeyFetch = null;
      });
  }
  return pendingKeyFetch;
}

// RSA-OAEP encrypts the password client-side so it never crosses the network
// as plaintext; the backend decrypts it with the matching private key before
// hashing/comparing (see api/libs/helpers/rsaHelper.ts).
export async function encryptPassword(password: string): Promise<string> {
  // crypto.subtle only exists in secure contexts (HTTPS or localhost).
  if (!globalThis.crypto?.subtle) {
    throw new Error('Secure login requires HTTPS. Please open the site over https:// (or localhost).');
  }
  const key = await getPublicKey();
  const encoded = new TextEncoder().encode(password);
  const encrypted = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, encoded);
  return arrayBufferToBase64(encrypted);
}
